import type {
  JournalArchiveYearDTO,
  JournalDetailDTO,
  JournalEditionDTO,
  JournalListItemDTO,
  JournalPublisherDTO,
  JournalSettingsDTO,
  JournalsPaginatedResponse,
} from '~/shared/api/client/journals/journals.dto';
import {
  CLIENT_API_JOURNALS_KEY,
  CLIENT_API_JOURNALS_URL,
  CLIENT_API_JOURNAL_DETAIL_KEY,
  CLIENT_API_JOURNAL_DETAIL_URL,
  CLIENT_API_JOURNAL_EDITIONS_KEY,
  CLIENT_API_JOURNAL_EDITIONS_URL,
  CLIENT_API_JOURNAL_SETTINGS_KEY,
  CLIENT_API_JOURNAL_SETTINGS_URL,
  CLIENT_API_JOURNAL_YEARS_KEY,
  CLIENT_API_JOURNAL_YEARS_URL,
  JOURNAL_ISSUES_PAGE_SIZE,
} from '~/shared/api/client/journals/journals.const';
import {
  CLIENT_JOURNAL_ADVERTISING_KEY,
  CLIENT_JOURNAL_ADVERTISING_URL,
  CLIENT_JOURNAL_RECOMMENDED_KEY,
  CLIENT_JOURNAL_RECOMMENDED_URL,
} from '~/shared/api/client/publisher/publisher.const';
import type { NuxtApp } from '#app';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';

/**
 * The section's reads, one composable per thing a screen needs.
 *
 * They used to be a single `useJournalData` handing out every issue at once,
 * which is what a mock can afford: filtering by edition, counting archive
 * years and paging the grid all happened over that one array. With 839 issues
 * behind the real endpoints each of those is a request of its own, with the
 * filter in the query — which is also why they are separate composables and
 * not one object with five fields.
 *
 * They are async and awaited by the components, reads that belong together
 * started with Promise.all. Nuxt would wait for them on the server even without
 * `await`; what the await buys is a response in hand by the time the composable
 * builds its own state from it — see `loaded` in useJournalIssues.
 */

// Tabs and chips are separate pages; refetching these held every click
const reuseFetched = (key: string, nuxtApp: NuxtApp) =>
  nuxtApp.payload.data[key];

/**
 * The section reference: tabs and editions in one flat array of published rows.
 * Every screen of the section needs it — the tab bar, the chips, the addresses
 * built from slugs — so it carries one key and is fetched once per visit.
 *
 * It also has to resolve before the issues are asked for: the address names a
 * tab or an edition by slug, and which one that is decides the filter of the query
 */
export async function useJournalEditions() {
  const { data, status } = await useCustomFetch<JournalEditionDTO[]>(
    CLIENT_API_JOURNAL_EDITIONS_URL,
    {
      key: CLIENT_API_JOURNAL_EDITIONS_KEY,
      method: 'GET',
      watch: false,
      getCachedData: reuseFetched,
    },
  );

  const editions = computed<JournalEditionDTO[]>(() => data.value ?? []);
  const isLoading = computed(() => status.value === 'pending');

  return { editions, isLoading };
}

interface JournalIssuesParams {
  /** Issues of one edition; without it the whole tab's */
  edition?: () => number | undefined;
  /** Issues of every edition of one tab, by the tab id */
  tab?: () => number | undefined;
  year?: () => number | undefined;
  lazy?: boolean;
}

/**
 * A page of the issues grid, newest first. The grid grows by "Загрузить еще"
 * rather than replacing itself, so pages accumulate here and the caller sees
 * one list plus whether the endpoint has more
 */
export async function useJournalIssues(params: JournalIssuesParams = {}) {
  const page = ref(1);

  const edition = computed(() => params.edition?.());
  const tab = computed(() => params.tab?.());
  const year = computed(() => params.year?.());

  const requestParams = computed(() => ({
    page: page.value,
    page_size: JOURNAL_ISSUES_PAGE_SIZE,
    edition: edition.value,
    tab: tab.value,
    year: year.value,
  }));

  const { data, status } = await useCustomFetch<
    JournalsPaginatedResponse<JournalListItemDTO>
  >(CLIENT_API_JOURNALS_URL, {
    key: CLIENT_API_JOURNALS_KEY,
    method: 'GET',
    watch: [page, edition, tab, year],
    params: requestParams,
    lazy: params.lazy,
  });

  // Seeded from the first response, not only through the watcher below. A
  // watcher created in the synchronous part of a component's setup does not
  // run on the server once the data arrives, so a grid filled by it alone went
  // out empty in the html and appeared only after hydration — the archive under
  // an issue page did exactly that
  const loaded = ref<JournalListItemDTO[]>(data.value?.results ?? []);

  watch(data, (value) => {
    // Failed request: the seed under the shared key is the previous page's
    if (!value) {
      if (page.value === 1) loaded.value = [];
      return;
    }

    // Page 1 is a new selection, the rest extend the one on screen
    loaded.value =
      page.value === 1 ? value.results : [...loaded.value, ...value.results];
  });

  // Another edition or year picked: start the list over
  watch([edition, tab, year], () => {
    page.value = 1;
    loaded.value = [];
  });

  const issues = computed(() => loaded.value);
  const total = computed(() => data.value?.count ?? 0);
  const hasMore = computed(() => Boolean(data.value?.next));
  const isLoading = computed(() => status.value === 'pending');
  const isLoadingFirstPage = computed(
    () => isLoading.value && page.value === 1,
  );

  const loadMore = () => {
    if (!hasMore.value || isLoading.value) return;

    page.value += 1;
  };

  return { issues, total, hasMore, isLoading, isLoadingFirstPage, loadMore };
}

/** One issue by the id in its address — /journal/cre/i884 */
export async function useJournalIssue(id: () => number | null) {
  const issueId = computed(() => id());

  // A key per issue, or the previous one flashes in until the fetch
  const { data, status, error } = await useCustomFetch<JournalDetailDTO>(
    () => `${CLIENT_API_JOURNAL_DETAIL_URL}${issueId.value}/`,
    {
      key: `${CLIENT_API_JOURNAL_DETAIL_KEY}_${issueId.value}`,
      method: 'GET',
      watch: [issueId],
      immediate: issueId.value !== null,
      lazy: true,
    },
  );

  const issue = computed<JournalDetailDTO | null>(() => data.value ?? null);
  const isLoading = computed(() => status.value === 'pending');

  return { issue, isLoading, error };
}

/**
 * Years of the "Архив журнала" chips with the issue count of each. Counted by
 * the endpoint rather than derived from a list: a year with no issue must not
 * become a chip that leads nowhere, and the client never holds every issue
 */
export async function useJournalYears(editionId: () => number | undefined) {
  const edition = computed(() => editionId());

  const { data, status } = await useCustomFetch<JournalArchiveYearDTO[]>(
    CLIENT_API_JOURNAL_YEARS_URL,
    {
      key: CLIENT_API_JOURNAL_YEARS_KEY,
      method: 'GET',
      watch: [edition],
      params: computed(() => ({ edition: edition.value })),
    },
  );

  const years = computed<JournalArchiveYearDTO[]>(() => data.value ?? []);
  const isLoading = computed(() => status.value === 'pending');

  return { years, isLoading };
}

/** Advertising contacts of the black block, a singleton the editors keep */
export async function useJournalSettings() {
  const { data } = await useCustomFetch<JournalSettingsDTO>(
    CLIENT_API_JOURNAL_SETTINGS_URL,
    {
      key: CLIENT_API_JOURNAL_SETTINGS_KEY,
      method: 'GET',
      watch: false,
      getCachedData: reuseFetched,
    },
  );

  const settings = computed<JournalSettingsDTO>(
    () =>
      data.value ?? {
        advertising_email: '',
        advertising_phone: '',
      },
  );

  return { settings };
}

/**
 * A journal publisher block. Both of them come from /main/publishers/ like
 * every other publisher, told apart by type: "Рекомендации" picks issues for
 * the "Вам может быть интересно" grid, "Реклама" picks whole editions
 */
async function useJournalPublisher(url: string, key: string) {
  const { data } = await useCustomFetch<JournalPublisherDTO[]>(url, {
    key,
    method: 'GET',
    watch: false,
    getCachedData: reuseFetched,
  });

  // The endpoint answers with a list, but a journal publisher is one block
  return computed<JournalPublisherDTO | null>(() => data.value?.[0] ?? null);
}

export function useJournalRecommended() {
  return useJournalPublisher(
    CLIENT_JOURNAL_RECOMMENDED_URL,
    CLIENT_JOURNAL_RECOMMENDED_KEY,
  );
}

export function useJournalAdvertising() {
  return useJournalPublisher(
    CLIENT_JOURNAL_ADVERTISING_URL,
    CLIENT_JOURNAL_ADVERTISING_KEY,
  );
}
